import React from "react";
import BlockContent from "@sanity/block-content-to-react";
import styled from "styled-components";
import { warmPalette, neutralPalette } from "../../styles/abstracts/_variables.styles";
import { sanitySrcUrl } from "../../lib/libFunctions";

const PortableTextButton = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 4.4rem;
  margin: 0.75rem 0.75rem 0.75rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.2rem;
  background: ${warmPalette.primary};
  /* Article prose links must not override this button treatment. */
  && {
    color: ${neutralPalette.white};
    text-decoration: none;
  }
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: normal;
  line-height: 1.4;
  text-transform: none;
  transition: background 180ms ease, transform 180ms ease;

  &:hover,
  &:focus-visible {
    background: ${warmPalette.primaryDark};
    color: ${neutralPalette.white};
    transform: translateY(-1px);
  }
`;

export const blockSerializers = {
  types: {
    // The Studio's rich-text image type is named "photo".
    photo: ({ node }) => {
      const src = sanitySrcUrl(node);
      if (!src) return null;
      const dimensions = node.asset?._ref?.match(/-(\d+)x(\d+)-[^-]+$/);
      return (
        <figure style={{ margin: "2rem 0" }}>
          <img
            src={src}
            alt={node.alt || ""}
            width={dimensions ? Number(dimensions[1]) : undefined}
            height={dimensions ? Number(dimensions[2]) : undefined}
            loading="lazy"
            decoding="async"
            style={{ display: "block", width: "100%", height: "auto" }}
          />
        </figure>
      );
    },
    block: (props) => {
      const { style = "normal" } = props.node;

      if (/^h\d/.test(style)) {
        const level = style.replace(/[^\d]/g, "");
        return React.createElement(
          style,
          { className: `heading-${level}` },
          props.children
        );
      }

      if (style === "blockquote") {
        return <blockquote>- {props.children}</blockquote>;
      }

      // Fall back to default handling
      return BlockContent.defaultSerializers.types.block(props);
    },
    code: (props) => (
      <pre data-language={props.node.language}>
        <code>{props.node.code}</code>
      </pre>
    ),
  },
  list: (props) =>
    props.type === "bullet" ? (
      <ul>{props.children}</ul>
    ) : (
      <ol>{props.children}</ol>
    ),
  listItem: (props) =>
    props.type === "bullet" ? (
      <li>{props.children}</li>
    ) : (
      <li>{props.children}</li>
    ),
  marks: {
    strong: (props) => <strong>{props.children}</strong>,
    em: (props) => <em>{props.children}</em>,
    code: (props) => <code>{props.children}</code>,
    link: ({ mark, children }) => {
      const href =
        mark.linkType === "external"
          ? mark.url
          // Resolve this listing by its stable ID in raw and expanded CMS references.
          : (mark.reference?._ref || mark.reference?._id) === "listing-sookla"
            ? "/eat-drink/sookla"
            : `/${mark.reference?.slug?.current || ""}`;

      if (mark.isButton) {
        return (
          <PortableTextButton
            href={href}
            {...(mark.linkType === "external"
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {children}
          </PortableTextButton>
        );
      }

      if (mark.linkType === "external") {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }

      return <a href={href}>{children}</a>;
    },
  },
};
